import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import type { IObservableArray } from 'mobx'
import type moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import type Employee from 'modules/employees/store/models/Employee'
import SupplementObservationForm from 'modules/payroll_bundle/lib/SupplementObservationForm'
import SupplementValidationForm from 'modules/payroll_bundle/lib/SupplementValidationForm'

import ObservationForm from './ObservationForm'
import SupplementForm from './SupplementForm'
import sortSupplements from './sortSupplements'

class SupplementsFormStore {
  field: Record<string, any> = {}
  forms: IObservableArray<SupplementForm> = [] as any
  observationForms: IObservableArray<ObservationForm> = [] as any
  supplementValidationForms: IObservableArray<SupplementValidationForm> = [] as any
  supplementObservationForms: IObservableArray<SupplementObservationForm> = [] as any

  constructor() {
    makeObservable(this, {
      field: observable,
      forms: observable,
      formsByEmployee: computed,
      observationForms: observable,
      supplementValidationForms: observable,
      supplementObservationForms: observable,
      addSupplementValidationForm: action,
      addsupplementObservationForm: action,
    })
  }

  buildForms(supplements: any) {
    this.forms = supplements.map(
      (supplement: Record<string, any>) => new SupplementForm(supplement)
    )
  }

  buildObservationForms(observations: any) {
    this.observationForms = observations.map(
      (observation: Record<string, any>) => new ObservationForm(observation)
    )
  }

  buildSupplementObservationForms(supplementObservations: any) {
    this.supplementObservationForms = supplementObservations.map(
      (observation: Record<string, any>) => new SupplementObservationForm(observation)
    )
  }

  removeSupplementObservationForm(supplementObservationId: number) {
    this.supplementObservationForms = this.supplementObservationForms.filter(
      (observation) => +observation.get('id').value !== supplementObservationId
    ) as any
  }

  buildSupplementValidationForms(supplementValidations: any) {
    this.supplementValidationForms = supplementValidations.map(
      (validation: Record<string, any>) =>
        new SupplementValidationForm({
          employee_id: null,
          id: validation.get('id'),
          author_id: validation.get('author_id'),
          supplement_id: validation.get('supplement_id'),
        })
    )
  }

  removeSupplementValidationForm(id: number) {
    this.supplementValidationForms = this.supplementValidationForms.filter(
      (validation) => +validation.get('id').value !== id
    ) as any
  }

  supplementValidationFor(
    employee: Employee | undefined,
    supplementId: number
  ): SupplementValidationForm {
    let supplementValidationForm = this.supplementValidationForms.find(
      (validationForm) => validationForm.data().supplement_id === supplementId
    )

    if (!supplementValidationForm) {
      supplementValidationForm = new SupplementValidationForm({
        id: null,
        supplement_id: supplementId,
        employee_id: employee?.id,
      })
      this.addSupplementValidationForm(supplementValidationForm)
    }

    return supplementValidationForm
  }

  addForm(
    employee: Employee,
    effectiveOn: moment.Moment,
    supplementFormParams?: Partial<SupplementForm>
  ) {
    const defaultFormParams = {
      amount_in_cents: 0,
      unit: 'money',
      effective_on: dateToString(effectiveOn, 'backend'),
      employee_id: employee.attributes.id,
      created_at: new Date().toISOString(),
    }

    const checkSupplementObjectParams =
      supplementFormParams && Object.keys(supplementFormParams).length > 0

    if (checkSupplementObjectParams && supplementFormParams) {
      const taxonomy = supplementFormParams.contractTaxonomyId
      const createdBy = supplementFormParams.createdByAccessId
      const supplementData = supplementFormParams.supplementData

      const supplementDataWithTaxonomy = {
        ...supplementData,
        contracts_taxonomy_id: taxonomy,
        created_by_access_id: createdBy,
      }

      const formParams = { ...defaultFormParams, ...supplementDataWithTaxonomy }

      this.forms.unshift(new SupplementForm(formParams))
    } else {
      this.forms.unshift(new SupplementForm(defaultFormParams))
    }
  }

  addSupplementValidationForm(supplementValidationForm: SupplementValidationForm) {
    this.supplementValidationForms.push(supplementValidationForm)
  }

  removeForm(form: SupplementForm) {
    _.remove(this.forms, (f) => f === form)
  }

  removeForms(ids: number[]) {
    this.forms
      .filter((form) => ids.includes(parseInt(form.get('id').value, 10)))
      .forEach((form) => {
        this.removeForm(form)
      })
  }

  replaceForm(oldForm: SupplementForm, newForm: SupplementForm) {
    const index = this.forms.indexOf(oldForm)
    this.forms.splice(index, 1, newForm)
  }

  createEmptyObservationForm(
    employee: Employee,
    date: moment.Moment,
    policyPeriodId: number | null
  ): ObservationForm {
    const observationForm = new ObservationForm({
      id: null,
      observation_text: '',
      effective_on: dateToString(date, 'backend'),
      employee_id: employee.id,
      payroll_policy_period_id: policyPeriodId,
    })
    this.observationForms.push(observationForm)

    return observationForm
  }

  observationFor(employee: Employee): ObservationForm | null | undefined {
    let observationForm = null
    observationForm = this.observationForms.find(
      (observation) => parseInt(observation.get('employee_id').value, 10) === employee.id
    )

    return observationForm
  }

  supplementObservationFor(supplementId: number): SupplementObservationForm {
    let supplementObservationForm = this.supplementObservationForms.find(
      (supplementObservation) => supplementObservation.data().supplement_id === supplementId
    )

    if (!supplementObservationForm) {
      supplementObservationForm = new SupplementObservationForm({
        id: null,
        supplement_id: supplementId,
        text: '',
      })

      this.addsupplementObservationForm(supplementObservationForm)
    }

    return supplementObservationForm
  }

  addsupplementObservationForm(supplementObservationForm: SupplementObservationForm) {
    this.supplementObservationForms.push(supplementObservationForm)
  }

  formsFor(employee: Employee, _effectiveOn: moment.Moment): SupplementForm[] {
    const employeeForms = this.formsByEmployee[employee.attributes.id] || []
    return employeeForms.sort((f1: SupplementForm, f2: SupplementForm) => {
      const data1 = f1.data()
      const data2 = f2.data()

      return sortSupplements(
        {
          id: data1.id,
          variableId: data1.variable_compensation_id,
        },
        {
          id: data2.id,
          variableId: data2.variable_compensation_id,
        }
      )
    })
  }

  get formsByEmployee() {
    const formsIndexed = _.groupBy(this.forms, (f) => f.data().employee_id)

    return formsIndexed
  }
}

export default SupplementsFormStore
